import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useModal } from '../../hooks/useModal';
import AnalyticsDashboard from '../../components/analytics/component/AnalyticsDashboard';
import PerformanceInsights from '../../components/analytics/component/PerformanceInsights';
import GoalTracker from '../../components/analytics/GoalTracker';
import FeedbackForm from '../../components/feedback/FeedbackForm';
import VAHeaderNav from '../../components/navigation/VAHeaderNav';
import Button from '../../components/ui/Button';
import InfoModal from '../../components/ui/InfoModal';
import analyticsService from '../../utils/analyticsService';
import feedbackService from '../../utils/feedbackService';
import { supabase } from '../../utils/supabase';
import { BarChart3, Lightbulb, Target } from 'lucide-react';

const AnalyticsPage = () => {
  const { user: currentUser, userProfile } = useAuth();
  const { infoModal, showInfo, closeInfo } = useModal();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState('general');

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'insights', label: 'Insights', icon: Lightbulb },
    { id: 'goals', label: 'Goals', icon: Target }
  ];

  const handleFeedbackSubmit = async (feedbackData) => {
    try {
      const result = await feedbackService.submitFeedback({
        userId: userProfile.id,
        type: feedbackType,
        rating: feedbackData.rating,
        comment: feedbackData.comment,
        contextData: {
          page: 'analytics',
          timestamp: new Date().toISOString()
        }
      });
      
      if (result.success) {
        setShowFeedbackModal(false);
        
        showInfo({
          title: 'Thank You',
          message: 'Thank you for your feedback! We appreciate your input and will review it soon.',
          type: 'success'
        });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showInfo({
        title: 'Error',
        message: 'There was an error submitting your feedback. Please try again.',
        type: 'error'
      });
    }
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'dashboard':
        return <AnalyticsDashboard />;
      case 'insights':
        return <PerformanceInsights />;
      case 'goals':
        return <GoalTracker />;
      default:
        return <AnalyticsDashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <VAHeaderNav 
        title="Analytics & Performance" 
        subtitle="Track your progress and optimize your success" 
      />
      
      {/* Action Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="text-sm text-gray-600">
              Monitor your performance metrics and get insights to improve your success
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setFeedbackType('general');
                  setShowFeedbackModal(true);
                }}
              >
                💬 Give Feedback
              </Button>
              {/* <Button variant="outline">
                📤 Export Data
              </Button> */}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon size={14} className="inline-block mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderActiveTab()}
      </div>

      {/* Quick Actions Sidebar */}
      <div className="fixed bottom-6 right-6 space-y-3">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-xs">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Actions</h3>
          <div className="space-y-2">
            <button
              onClick={() => {
                setFeedbackType('job_application');
                setShowFeedbackModal(true);
              }}
              className="w-full text-left text-sm text-gray-700 hover:text-blue-600 transition-colors"
            >
              📝 Rate Recent Application
            </button>
            <button
              onClick={() => {
                setFeedbackType('interview');
                setShowFeedbackModal(true);
              }}
              className="w-full text-left text-sm text-gray-700 hover:text-blue-600 transition-colors"
            >
              ⭐ Rate Recent Interview
            </button>
            <button
              onClick={() => {
                setFeedbackType('employer_interaction');
                setShowFeedbackModal(true);
              }}
              className="w-full text-left text-sm text-gray-700 hover:text-blue-600 transition-colors"
            >
              💼 Rate Employer Experience
            </button>
            <button
              onClick={() => setActiveTab('goals')}
              className="w-full text-left text-sm text-gray-700 hover:text-blue-600 transition-colors"
            >
              🎯 Update Goal Progress
            </button>
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackForm
          type={feedbackType}
          contextId={null}
          contextTitle={null}
          onSubmit={handleFeedbackSubmit}
          onCancel={() => setShowFeedbackModal(false)}
        />
      )}

      {/* Welcome Tips for First-Time Users */}
      {currentUser?.isFirstTimeAnalytics && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="text-center">
              <span className="text-4xl mb-4 block">📊</span>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Welcome to Analytics!</h2>
              <p className="text-gray-600 mb-6">
                Track your performance, get AI-powered insights, and set goals to boost your success as a Virtual Assistant.
              </p>
              <div className="space-y-3 text-left mb-6">
                <div className="flex items-start space-x-3">
                  <span className="text-lg">📈</span>
                  <div>
                    <h3 className="font-medium text-gray-900">Dashboard</h3>
                    <p className="text-sm text-gray-600">View your key metrics and performance trends</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-lg">💡</span>
                  <div>
                    <h3 className="font-medium text-gray-900">Insights</h3>
                    <p className="text-sm text-gray-600">Get AI-powered recommendations to improve</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-lg">🎯</span>
                  <div>
                    <h3 className="font-medium text-gray-900">Goals</h3>
                    <p className="text-sm text-gray-600">Set and track your professional development goals</p>
                  </div>
                </div>
              </div>
              <Button
                onClick={async () => {
                  // Mark user as having seen analytics intro
                  try {
                    // This would typically update user preferences in the database
                    const { error } = await supabase
                      .from('user_profiles')
                      .update({ isFirstTimeAnalytics: false })
                      .eq('id', userProfile.id);
                      
                    if (error) throw error;
                    console.log('Analytics intro completed');
                  } catch (err) {
                    console.error('Error updating user preferences:', err);
                  }
                }}
                className="w-full"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Info Modal */}
      {infoModal && (
        <InfoModal
          title={infoModal.title}
          message={infoModal.message}
          type={infoModal.type}
          buttonLabel={infoModal.buttonLabel}
          onClose={closeInfo}
        />
      )}
    </div>
  );
};

export default AnalyticsPage;


